// =============================================================================
// OrganizationPicker — searchable async organization combobox
// =============================================================================
// Replaces the plain <Select> org filter (which was capped at the first 100
// organizations and not searchable). Types a debounced server-side search
// against GET /api/v1/admin/organizations?search=... and writes the chosen
// org id through onChange. When a value arrives from the URL (deep link) the
// selected org's name is resolved with a single findOne fetch.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useApiClient } from '@tkpod/api-client';
import { useDebounce } from '../hooks/use-debounce';

interface OrgOption {
  id: string;
  name: string;
}

export interface OrganizationPickerProps {
  label?: string;
  /** Selected organization id ('' = none). */
  value: string;
  onChange: (organizationId: string) => void;
}

export function OrganizationPicker({ label, value, onChange }: OrganizationPickerProps) {
  const api = useApiClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const optionsQuery = useQuery({
    queryKey: ['admin', 'organizations', 'picker', debouncedQuery],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '20' });
      if (debouncedQuery.trim()) params.set('search', debouncedQuery.trim());
      return api.get<{ data: OrgOption[] }>(`/api/v1/admin/organizations?${params.toString()}`);
    },
    staleTime: 30_000,
    enabled: open,
  });

  // Resolve the selected org's display name — the id may come from a URL
  // deep link, so it is not guaranteed to be inside the searched page.
  const selectedQuery = useQuery({
    queryKey: ['admin', 'organizations', 'picker-selected', value],
    queryFn: () => api.get<{ data: OrgOption }>(`/api/v1/admin/organizations/${value}`),
    staleTime: 60_000,
    enabled: Boolean(value),
  });

  const options = useMemo(() => optionsQuery.data?.data ?? [], [optionsQuery.data]);
  const selectedName = value
    ? (selectedQuery.data?.data?.name ?? options.find((o) => o.id === value)?.name ?? '…')
    : '';

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectOption = (id: string) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}
      <div className="relative">
        {value ? (
          <div className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary">
            <span className="min-w-0 truncate">{selectedName}</span>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(true);
                // Re-focus the search input once it mounts.
                requestAnimationFrame(() => inputRef.current?.focus());
              }}
              aria-label="Clear organization filter"
              className="shrink-0 rounded p-0.5 text-text-tertiary transition-colors hover:bg-surface-secondary hover:text-text-primary"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-label={label ?? 'Search organizations'}
            placeholder="Search organizations…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className={clsx(
              'block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary',
              'transition-colors duration-150 placeholder:text-text-tertiary',
              'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
            )}
          />
        )}

        {open && !value && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
            {optionsQuery.isLoading ? (
              <p className="px-3 py-2.5 text-sm text-text-tertiary">Loading…</p>
            ) : optionsQuery.isError ? (
              <p className="px-3 py-2.5 text-sm text-danger">Failed to load organizations.</p>
            ) : options.length === 0 ? (
              <p className="px-3 py-2.5 text-sm text-text-tertiary">
                No organizations found{debouncedQuery ? ` for “${debouncedQuery}”` : ''}.
              </p>
            ) : (
              <ul>
                {options.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => selectOption(o.id)}
                      className="block w-full truncate px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-secondary"
                    >
                      {o.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
